import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { CorporationJoinMode } from '@kcftech/prometheustypescriptapiclient';
import { Detail } from 'components/activity/Detail';
import { IconButton } from 'components/button/IconButton';
import { DocumentTitle } from 'components/DocumentTitle';
import { CollapsiblePane } from 'components/expandable_pane/CollapsiblePane';
import { DetailsPage } from 'components/page/DetailsPage';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { usePermissions } from 'contexts/PermissionsContext';
import { useUser } from 'contexts/UserContext';
import { useCorporation } from 'custom_hooks/data_hooks/corporation/useCorporation';
import { useCorporationKcfInfo } from 'custom_hooks/data_hooks/corporation/useCorporationKcfInformation';
import { usePagedUsersDataSource } from 'custom_hooks/data_hooks/data_source/usersDataSource';
import { useValueAsTuple } from 'custom_hooks/useValueAsTuple';
import { MediaSize, useIsViewportAtLeast } from 'custom_hooks/useWindowSize';
import { AccessLevelDropdown } from 'pages/Settings/Corporations/AccessLevelDropdown';
import { CorporationIconDetails } from 'pages/Settings/Corporations/CorporationIconDetails';
import { CorporationNetSuiteIdInlineEditor } from 'pages/Settings/Corporations/CorporationNetSuiteIdInlineEditor';
import { CorporationAbbreviationCell } from 'pages/Settings/Corporations/table_cells/CorporationAbbreviationCell';
import { CorporationActiveCell } from 'pages/Settings/Corporations/table_cells/CorporationActiveCell';
import { CorporationIndustryCell } from 'pages/Settings/Corporations/table_cells/CorporationIndustryCell';
import { CorporationNameCell } from 'pages/Settings/Corporations/table_cells/CorporationNameCell';
import { SettingsHeading } from 'pages/Settings/shared/styled';
import { CreateUsersModal } from 'pages/Settings/Users/CreateUsersModal';
import { UsersTable } from 'pages/Settings/Users/UsersTable';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

const DetailRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
`;

const DetailColumn = styled.div`
    display: flex;
    flex-direction: column;
    flex-basis: 100%;
    flex: 1;
`;

export type SingleCorporationPageProps = Readonly<{
    corporationId: string;
}>;

export function SingleCorporationPage({ corporationId }: SingleCorporationPageProps) {
    const user = useUser();
    const [usersPaneOpen, setUsersPaneOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const {
        canEditCorporationIcon,
        canEditUserCorporationAssignment,
        canViewKcfInfo,
    } = usePermissions();
    const viewportAtLeastMed = useIsViewportAtLeast(MediaSize.medium);

    const { data: corporation, refetch, isLoading } = useCorporation(corporationId);
    const corporationIds = useValueAsTuple(corporationId);

    const reload = () => {
        refetch();
        user.updateFavorites();
    };

    const corporationUsersDataSource = usePagedUsersDataSource({
        name: 'corporation-users',
        defaultFilter: {
            corporationIds,
            nameSearch: '',
            corporationJoinMode: CorporationJoinMode.CanViewWithin,
        },
    });

    const { setFilter } = corporationUsersDataSource;

    const onUserCreate = () => {
        setAddModalOpen(false);
        corporationUsersDataSource.reloadPage();
    };

    useEffect(() => {
        setFilter((current) => ({
            ...current,
            corporationIds,
        }));
    }, [corporationIds, setFilter]);

    return (
        <DetailsPage isLoading={isLoading} fullHeight fullWidth noPageScroll>
            {corporation && (
                <DocumentTitle title={`${corporation.Name} Corporation Settings`} />
            )}
            {corporation && (
                <div className="flex-wrapper p-35" style={{ overflowY: 'auto' }}>
                    <DetailRow>
                        <DetailColumn>
                            <SettingsHeading>
                                <CorporationNameCell
                                    rowData={corporation}
                                    onChange={reload}
                                />
                            </SettingsHeading>
                            <div className="py-20">
                                <Detail label="Abbreviation" labelClassName="text-nowrap">
                                    <CorporationAbbreviationCell
                                        rowData={corporation}
                                        onChange={reload}
                                    />
                                </Detail>
                                <Detail label="Industry" labelClassName="text-nowrap">
                                    <CorporationIndustryCell
                                        rowData={corporation}
                                        onChange={reload}
                                    />
                                </Detail>
                                <Detail label="Active">
                                    <CorporationActiveCell
                                        rowData={corporation}
                                        onChange={reload}
                                    />
                                </Detail>
                                <Detail label="Access Level">
                                    <AccessLevelDropdown
                                        corporation={corporation}
                                        onConfirm={reload}
                                    />
                                </Detail>
                                {canViewKcfInfo && (
                                    <KcfInfoSection corporationId={corporation.Id} />
                                )}
                            </div>
                        </DetailColumn>
                        <DetailColumn style={{ alignItems: 'flex-end' }}>
                            {canEditCorporationIcon && viewportAtLeastMed && (
                                <CorporationIconDetails
                                    corporation={corporation}
                                    onUpload={refetch}
                                />
                            )}
                        </DetailColumn>
                    </DetailRow>
                    <DetailRow>
                        <div style={{ width: '100%' }}>
                            <CollapsiblePane
                                title="Users"
                                expanded={usersPaneOpen}
                                onChange={setUsersPaneOpen}
                            >
                                <div className="p-20">
                                    <div className="flex-row justify-flex-end mb-15">
                                        {canEditUserCorporationAssignment(
                                            corporationId
                                        ) && (
                                            <IconButton
                                                icon={faPlus}
                                                onClick={() => setAddModalOpen(true)}
                                            >
                                                Add User
                                            </IconButton>
                                        )}
                                    </div>
                                    <UsersTable
                                        dataSource={corporationUsersDataSource}
                                        corporationId={corporationId}
                                    />
                                </div>
                            </CollapsiblePane>
                        </div>
                    </DetailRow>
                </div>
            )}
            {addModalOpen && (
                <CreateUsersModal
                    corporation={corporation}
                    onCreate={onUserCreate}
                    onClose={() => setAddModalOpen(false)}
                />
            )}
        </DetailsPage>
    );
}

const KcfInfoSection = ({
    corporationId,
    onChange,
}: {
    corporationId: string;
    onChange?: () => void;
}) => {
    const { data: kcfInformation, isLoading, refetch } = useCorporationKcfInfo(
        corporationId
    );

    return (
        <SpinnerArea noContainer isLoading={isLoading}>
            {kcfInformation && (
                <Detail label="NetSuite ID">
                    <CorporationNetSuiteIdInlineEditor
                        corporationId={corporationId}
                        corporationKcfInfo={kcfInformation}
                        onChange={() => {
                            refetch();
                            onChange?.();
                        }}
                    />
                </Detail>
            )}
        </SpinnerArea>
    );
};
